import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFetch from '../../hooks/useFetch';
import { isUndefined } from '../../predicates/type';
import { CacheManager } from '../../utils/cacheManager';
import { createProvider } from '../../utils/createProvider';
import {
	STYLE_CONTEXT_CACHE_TIMEOUT,
	STYLE_CONTEXT_PERSIST_KEY,
	STYLE_CONTEXT_URL,
	styleContextDefaultState,
} from './const';
import { StyleContextAPIProps, StyleContextProps, StyleContextResponse } from './types';
import { getStyleContextFromDOM } from './utils';

const [StyleProvider, useStyleContextState] = createProvider<StyleContextProps>({
	contextName: 'StyleContext',
});

const [StyleAPIProvider, useStyleContextAPI] = createProvider<StyleContextAPIProps>({
	contextName: 'StyleContextAPI',
});

const StyleContextProvider: FC<React.PropsWithChildren> = ({ children }) => {
	const cacheManagerRef = useRef(
		new CacheManager(STYLE_CONTEXT_PERSIST_KEY, STYLE_CONTEXT_CACHE_TIMEOUT, {
			resetStartDateImmediately: true,
		}),
	);

	const { fetch, response, isPending } = useFetch<StyleContextResponse>(STYLE_CONTEXT_URL);
	const fetchPendingRef = useRef(isPending);
	const shouldFetch = useCallback(() => {
		if (fetchPendingRef.current) {
			return false;
		}

		return cacheManagerRef.current.hasInvalidateCacheTime();
	}, []);
	const [styleContext, setStyleContext] = useState(() => {
		const styleContextFromDOM = getStyleContextFromDOM();

		return {
			...styleContextDefaultState,
			...styleContextFromDOM,
			isInitialized: true,
		};
	});
	const tryFetch = useCallback(() => {
		if (shouldFetch()) {
			fetch();
		}
	}, [shouldFetch, fetch]);

	useEffect(() => {
		fetchPendingRef.current = isPending;
	}, [isPending]);

	useEffect(() => {
		const responseData = response.data;

		if (isUndefined(responseData)) {
			return;
		}

		window.localStorage.setItem(STYLE_CONTEXT_PERSIST_KEY, JSON.stringify(responseData));
		cacheManagerRef.current.resetStartDate();

		setStyleContext((prevValue) => ({
			...prevValue,
			...responseData,
			isInitialized: true,
		}));
	}, [response]);

	const styleContextApi = useMemo(
		() => ({
			fetchStyleContext: fetch,
			tryFetchStyleContext: tryFetch,
			resetStyleContextCache: () => {
				cacheManagerRef.current.resetStartDate();
			},
		}),
		[fetch, tryFetch],
	);

	return (
		<StyleProvider value={styleContext}>
			<StyleAPIProvider value={styleContextApi}>{children}</StyleAPIProvider>
		</StyleProvider>
	);
};

export { StyleContextProvider, useStyleContextState, useStyleContextAPI };
